import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import type { IDonorCardDetail } from '@/entities/donor/model'
import { examinationFormSchema } from '@/entities/visit/model/examination.schema'
import { ETemperatureRecommendation } from '@/entities/visit/model/visits.type'
import type { ExaminationFormValues } from '@/entities/visit/model/examination.schema'
import { useUpdateVisitExaminationMutation } from '@/entities/visit/api'
import { toaster } from '@/shared/ui'
import { getAxiosErrorData } from '@/shared/utils'
import type {
  IApiError,
  IApiErrorField,
  IApiResponse,
} from '@/shared/types/api'

interface Options {
  visitId: string
  donorId: string
  donorCard?: IDonorCardDetail
}

export function useMedicalHistoryForm({
  visitId,
  donorId,
  donorCard,
}: Options) {
  const { t } = useTranslation()
  const schema = useMemo(() => examinationFormSchema(t), [t])

  const form = useForm<ExaminationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      weight_kg: '',
      height_cm: '',
      blood_pressure_systolic: '',
      blood_pressure_diastolic: '',
      pulse: '',
      temperature_c: '',
      conclusion: '',
      needs_ambulatory_exam: false,
      referral_organization_id: '',
    },
  })

  const { mutateAsync: updateExamination, isPending } =
    useUpdateVisitExaminationMutation(visitId, donorId)

  useEffect(() => {
    if (!donorCard) return
    form.reset({
      weight_kg: '',
      height_cm: '',
      blood_pressure_systolic: '',
      blood_pressure_diastolic: '',
      pulse: '',
      temperature_c: '',
      conclusion: '',
      needs_ambulatory_exam: false,
      referral_organization_id: '',
    })
  }, [donorCard, form])

  const needsAmbulatoryExam = useWatch({
    control: form.control,
    name: 'needs_ambulatory_exam',
  })

  useEffect(() => {
    if (!needsAmbulatoryExam) {
      form.setValue('referral_organization_id', '')
    }
  }, [needsAmbulatoryExam, form])

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      const response = await updateExamination({
        weight_kg: Number(data.weight_kg),
        height_cm: Number(data.height_cm),
        blood_pressure_systolic: Number(data.blood_pressure_systolic),
        blood_pressure_diastolic: Number(data.blood_pressure_diastolic),
        pulse: Number(data.pulse),
        ...(data.temperature_c.trim() !== '' && {
          temperature_c: Number(data.temperature_c),
        }),
        conclusion: data.conclusion,
        needs_ambulatory_exam: data.needs_ambulatory_exam,
        referral_organization_id: data.referral_organization_id,
        status: 'COMPLETED',
      })
      toaster.success({ title: t('donor_detail.stats.save_success') })
      const recommendation = response.data.temperature_recommendation
      if (
        recommendation &&
        recommendation !== ETemperatureRecommendation.NORMAL
      ) {
        toaster.warning({
          title: t(
            `donor_detail.stats.temperature_recommendation.${recommendation}`,
            { defaultValue: recommendation },
          ),
        })
      }
    } catch (error) {
      const body = getAxiosErrorData<IApiResponse<null>>(error)
      if (body?.errors[0]?.message) {
        body.errors.forEach((error: IApiError) => {
          if (error.code === '200000') {
            error.fields.forEach((field: IApiErrorField) => {
              form.setError(field.key as keyof ExaminationFormValues & string, {
                message: field.value,
              })
            })
          } else {
            toaster.error({ title: error.message })
          }
        })
        return
      }
      toaster.error({ title: t('donor_detail.stats.save_error') })
    }
  })

  return { form, isPending, onSubmit, needsAmbulatoryExam }
}
